import { useAppDispatch, useAppSelector } from 'redux/store';
import { generatePath, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    getRestaurantId,
    getCurrentMenuId,
    getCurrentConsumptionModeType,
    getTableId,
} from 'redux/app';
import { getOrderCart, getOrderTotalInclTax, resetOrder } from 'redux/order';
import { useCreateOrderMutation } from 'services/order/order.endpoints';
import { getConsumptionModeList } from 'redux/consumptionModeList';
import { Routes } from 'core/routes.types';
import { computeCreateOrderPayload, getPaymentMethodId } from './utils';

export type PaymentStep = 'choose_payment_method' | 'stripe_order_confirmation';
export type PaymentMode = 'stripe' | 'ewallet';

export const usePaymentVM = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const restaurantId = useAppSelector(getRestaurantId);
    const currentMenuId = useAppSelector(getCurrentMenuId);
    const tableId = useAppSelector(getTableId);
    const orderCart = useAppSelector(getOrderCart);
    const totalInclTax = useAppSelector(getOrderTotalInclTax);
    const currentConsumptionModeType = useAppSelector(getCurrentConsumptionModeType);
    const consumptionModeList = useAppSelector(getConsumptionModeList);

    const [paymentStep, setPaymentStep] = useState<PaymentStep>('choose_payment_method');
    const [paymentMode, setPaymentMethod] = useState<PaymentMode>('stripe');
    const [customError, setCustomError] = useState<string | undefined>(undefined);

    const [
        createOrder,
        { data: orderCreated, isLoading: isLoadingCreateOrder, error: errorCreateOrder, isSuccess },
    ] = useCreateOrderMutation();

    const goBack = useCallback(() => {
        navigate(Routes.Cart);
    }, [navigate]);

    const handlePaymentModeChange = (mode: PaymentMode) => {
        setPaymentMethod(mode);
    };

    const handlePaymentModeConfirm = () => {
        paymentMode === 'stripe' && setPaymentStep('stripe_order_confirmation');
    };

    const resetPaymentStep = () => {
        setPaymentStep('choose_payment_method');
        setPaymentMethod('stripe');
        setCustomError(undefined);
    };

    const paymentMethodId: number | undefined = useMemo(
        () =>
            getPaymentMethodId({
                paymentMode,
                consumptionModeList,
                currentConsumptionModeType,
            }),
        [currentConsumptionModeType, consumptionModeList, paymentMode],
    );

    const handleStripePaymentSubmit = async (stripePaymentMethodId: string | undefined) => {
        setCustomError(undefined);

        if (
            restaurantId &&
            currentMenuId &&
            tableId &&
            totalInclTax &&
            paymentMethodId &&
            stripePaymentMethodId
        ) {
            const createorderPayload = computeCreateOrderPayload({
                orderCart,
                restaurantId,
                currentMenuId,
                tableId,
                paymentMethodId,
                totalInclTax,
                stripePaymentMethodId,
            });
            await createOrder(createorderPayload);
        } else {
            setCustomError(t('genericErrorMessage'));
        }
    };

    useEffect(() => {
        if (isSuccess && orderCreated?.orderId) {
            dispatch(resetOrder(undefined));
            navigate(
                generatePath(Routes.OrderConfirmation, { orderId: String(orderCreated.orderId) }),
            );
        }
        if (errorCreateOrder) {
            navigate(Routes.OrderError);
        }
    }, [orderCreated, isSuccess, errorCreateOrder, navigate, dispatch]);

    return {
        errorCreateOrder: customError,
        paymentStep,
        paymentMode,
        goBack,
        resetPaymentStep,
        handlePaymentModeConfirm,
        handlePaymentModeChange,
        handleStripePaymentSubmit,
        isLoadingCreateOrder,
    };
};
